import { useState, useEffect, useMemo, useCallback } from "react";
import { FaCheck, FaEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { saveToCache, readFromCache } from "../../../../../services/pathologyCache";
import ExportOptions from "../../../../ExportOptions";
import TableSkeleton from "../../../../TableSkeleton";
import ConfirmActionModal from "../../../../ConfirmDeleteModal";
import NicuOfficeExpenseModal from "./NicuOfficeExpenseModal";
import PageHeading from "../../../../PageHeading";
import DynamicSelect from "../../../../DynamicSelect";

const NicuOfficeExpenseList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [nicuOfflineActive, setNicuOfflineActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filterType, setFilterType] = useState("monthly");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const itemsPerPage = 10;
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const EXPENSE_URL = "https://api.muktihospital.com/api/nicu/office-expense";

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = { type: filterType };
    if (filterType === 'daily') { params.startDate = startDate; params.endDate = endDate; }
    else if (filterType === 'monthly') { const [y, m] = selectedMonth.split('-'); params.year = y; params.month = m; }
    else if (filterType === 'yearly') { params.year = selectedYear; }

    const cacheKey = `nicu_office_expenses_${JSON.stringify(params)}`;

    try {
      const response = await axios.get(EXPENSE_URL, { headers: { "x-api-key": API_KEY }, params });
      setExpenses(response.data.expenses || []);

      // Cache this filter for offline viewing.
      setNicuOfflineActive(false);
      saveToCache(cacheKey, { expenses: response.data.expenses || [] });
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = await readFromCache(cacheKey);
        if (cached && cached.expenses) {
          setExpenses(cached.expenses);
          setNicuOfflineActive(true);
          toast.info("Offline: showing saved expenses.");
        } else {
          setExpenses([]);
          setNicuOfflineActive(false);
          toast.info("Offline: this filter isn't saved yet. Open it online once.");
        }
      } else {
        toast.error("Failed to load data.");
        setExpenses([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filterType, startDate, endDate, selectedMonth, selectedYear, refreshTrigger]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredExpenses = expenses.filter(
    (item) =>
      (item.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.note || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);
  const totalAmount = filteredExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const currentData = filteredExpenses
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((item, index) => ({ ...item, displayId: (currentPage - 1) * itemsPerPage + index + 1 }));

  const handleSelectAll = (e) => { setSelectedRows(e.target.checked ? currentData.map((item) => item.id) : []); };
  const handleRowSelect = (id) => { setSelectedRows((prev) => prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]); };

  const handleDelete = async (ids) => {
    try {
      for (const id of ids) {
        await axios.delete(`${EXPENSE_URL}/delete/${id}`, { headers: { "x-api-key": API_KEY } });
      }
      toast.success("Deleted successfully!");
      setSelectedRows([]);
      setDeleteModalOpen(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      toast.error("Failed to delete.");
    }
  };

  const handleEdit = (item) => { setExpenseToEdit(item); setModalOpen(true); };

  const handleSaveExpense = async (savedItem, isEditMode) => {
    try {
      if (isEditMode) {
        await axios.put(`${EXPENSE_URL}/edit/${savedItem.id}`, savedItem, { headers: { "x-api-key": API_KEY } });
        toast.success("Updated successfully!");
      } else {
        await axios.post(`${EXPENSE_URL}/add`, savedItem, { headers: { "x-api-key": API_KEY } });
        toast.success("Added successfully!");
      }
      setModalOpen(false);
      setExpenseToEdit(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      toast.error("Failed to save.");
    }
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);
  const headers = ["Serial No", "Date", "Expense Item Name", "Amount", "Note"];
  const dataMapper = (item) => [item.displayId, new Date(item.createdAt).toLocaleDateString('en-GB'), item.name, item.amount, item.note].join("\t");
  const skeletonColumns = [{ width: "40px" }, { width: "100px" }, { width: "150px" }, { width: "80px" }, { width: "200px" }, { width: "80px" }];
  const breadcrumbs = useMemo(() => [{ label: "Dashboard", url: "/" }, { label: "NICU Office" }, { label: "Office Expenses" }], []);

  return (
    <div>
      <ToastContainer />
      <div className="print-hidden"><PageHeading title="NICU Office Expenses" breadcrumbs={breadcrumbs} /></div>
      {nicuOfflineActive && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2 print-hidden">
          <span>⚠️</span>
          <span>Offline mode — showing saved data. Connect to the internet for the latest.</span>
        </div>
      )}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="flex items-center justify-between gap-3">
            <div className="inline-flex items-center gap-3 shrink-0">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">Office Expenses List</h2>
              {selectedRows.length > 0 && (
                <button onClick={() => setDeleteModalOpen(true)} className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"><RiDeleteBin6Line /></button>
              )}
            </div>
            <input type="text" className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color" placeholder="Search item name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
          <div className="flex flex-col sm:flex-row items-start justify-start sm:justify-end gap-3 w-full sm:w-auto">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-32">
                <DynamicSelect options={filterOptions} value={filterOptions.find(o => o.value === filterType)} onChange={o => setFilterType(o.value)} isSearchable={false} placeholder="Filter" />
              </div>
              {filterType === "daily" && (
                <>
                  <div className="flex items-center gap-2"><label className="text-sm font-medium text-gray-600">From:</label><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="p-2 border rounded-md text-sm outline-none w-32" /></div>
                  <div className="flex items-center gap-2"><label className="text-sm font-medium text-gray-600">To:</label><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="p-2 border rounded-md text-sm outline-none w-32" /></div>
                </>
              )}
              {filterType === "monthly" && (<div className="flex items-center gap-2"><input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="p-2 border rounded-md text-sm outline-none" /></div>)}
              {filterType === "yearly" && (<div className="flex items-center gap-2"><input type="number" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="p-2 border rounded-md text-sm outline-none w-24" min="2000" max="2100" /></div>)}
            </div>
            <div className="flex items-center justify-between gap-3 sm:w-auto w-full">
              <button onClick={() => { setExpenseToEdit(null); setModalOpen(true); }} className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 px-4 py-2 text-sm">+ Add Bill</button>
              <ExportOptions data={currentData} dropdownOpen={dropdownOpen} toggleDropdown={toggleDropdown} title="NICU Office Expenses" headers={headers} dataMapper={dataMapper} />
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="px-4 py-2 flex justify-end items-center border-b border-gray-100 print-hidden">
          <span className="text-sm text-gray-500 mr-2">Total Expense:</span>
          <span className="text-lg font-black text-red-600">৳{totalAmount.toFixed(2)}</span>
        </div>

        <div className="overflow-x-auto print-wrapper">
          {loading ? (
            <div className="print-hidden"><table className="min-w-[600px] w-full"><TableSkeleton rowCount={itemsPerPage} columns={skeletonColumns} /></table></div>
          ) : filteredExpenses.length === 0 ? (
            <div className="p-5 text-center text-gray-600">No expenses found.</div>
          ) : (
            <table className="min-w-[600px] w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left print-hidden-in-print">
                    <div className="relative left-3 -top-[2px]">
                      <input type="checkbox" className="hidden peer print-hidden" onChange={handleSelectAll} checked={currentData.length > 0 && currentData.every((item) => selectedRows.includes(item.id))} id="selectAll" />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden"><FaCheck className="text-slate-100" /></span>
                      <label htmlFor="selectAll" className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"></label>
                    </div>
                  </th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Serial No</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Date</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Expense Item</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Amount</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Note</th>
                  <th className="p-3 text-right text-sm font-medium text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, index) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="text-left print-hidden-in-print">
                      <div className="relative left-3 -top-[3px]">
                        <input type="checkbox" id={`row-${index}`} className="hidden peer" checked={selectedRows.includes(item.id)} onChange={() => handleRowSelect(item.id)} />
                        <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden"><FaCheck className="text-slate-100" /></span>
                        <label htmlFor={`row-${index}`} className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"></label>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-gray-700">{item.displayId}</td>
                    <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{new Date(item.createdAt).toLocaleDateString("en-GB")}</td>
                    <td className="p-3 text-sm text-gray-700 capitalize">{item.name}</td>
                    <td className="p-3 text-sm text-gray-700 font-bold">{item.amount}</td>
                    <td className="p-3 text-sm text-gray-500 truncate max-w-[200px]">{item.note || "-"}</td>
                    <td className="p-3 text-right space-x-2">
                      <button onClick={() => handleEdit(item)} className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"><FaEdit /></button>
                      <button onClick={() => { setSelectedRows([item.id]); setDeleteModalOpen(true); }} className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"><RiDeleteBin6Line /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="p-4 flex justify-end gap-2">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="p-2 border rounded disabled:opacity-50"><MdKeyboardArrowLeft /></button>
            <span className="p-2 text-sm">Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="p-2 border rounded disabled:opacity-50"><MdKeyboardArrowRight /></button>
          </div>
        )}
      </div>

      <ConfirmActionModal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={handleDelete} ids={selectedRows} entityName="expense" />
      <NicuOfficeExpenseModal isOpen={modalOpen} onClose={() => { setModalOpen(false); setExpenseToEdit(null); }} onSave={handleSaveExpense} expenseToEdit={expenseToEdit} />
    </div>
  );
};

export default NicuOfficeExpenseList;